import { BACKEND_URL } from '../config';
import React, { useState } from 'react';
import { Leaf } from 'lucide-react';

const Auth = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loginMethod, setLoginMethod] = useState('email'); // 'email', 'mobile', or 'otp'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mobile: '',
    otp: '',
    language: 'en'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpData, setOtpData] = useState(null);

  const handleRequestOTP = async () => {
    if (!formData.mobile) {
      setError('Please enter mobile number');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mobile: formData.mobile })
      });

      const data = await response.json();

      if (response.ok) {
        setOtpSent(true);
        setOtpData(data);
        setSuccess(`OTP sent to ${formData.mobile}. OTP: ${data.otp} (for testing)`);
      } else {
        setError(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!formData.otp) {
      setError('Please enter OTP');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mobile: formData.mobile,
          otp: formData.otp
        })
      });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.token, data.user);
      } else {
        setError(data.detail || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      
      // Prepare data based on login method
      let submitData = { ...formData };
      if (isLogin) {
        if (loginMethod === 'email') {
          submitData = { email: formData.email, password: formData.password };
        } else if (loginMethod === 'mobile') {
          submitData = { mobile: formData.mobile, password: formData.password };
        }
      }
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.token, data.user);
      } else {
        setError(data.detail || 'Authentication failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-image">
          <img src="https://images.unsplash.com/photo-1615149596797-da241c6f8e30" alt="Agriculture" />
          <div className="auth-image-overlay">
            <div className="auth-branding">
              <Leaf size={64} />
              <h1>BoAI</h1>
              <p>Detect crop diseases instantly with AI</p>
              <p className="hindi-text">AI के साथ फसल रोगों का तुरंत पता लगाएं</p>
            </div>
          </div>
        </div>

        <div className="auth-form-section">
          <div className="auth-form-container">
            <div className="mobile-logo">
              <Leaf size={40} />
              <h2>BoAI</h2>
            </div>

            <h2 className="auth-title">{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
            <p className="auth-subtitle">
              {isLogin ? 'स्वागत है' : 'खाता बनाएं'}
            </p>

            {error && <div className="error-message" data-testid="error-message">{error}</div>}
            {success && <div className="success-message" data-testid="success-message">{success}</div>}

            {isLogin && loginMethod !== 'otp' && (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="login-method-toggle">
                  <button
                    type="button"
                    className={loginMethod === 'email' ? 'active' : ''}
                    onClick={() => setLoginMethod('email')}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    className={loginMethod === 'mobile' ? 'active' : ''}
                    onClick={() => setLoginMethod('mobile')}
                  >
                    Password
                  </button>
                  <button
                    type="button"
                    className={loginMethod === 'otp' ? 'active' : ''}
                    onClick={() => {
                      setLoginMethod('otp');
                      setOtpSent(false);
                      setError('');
                      setSuccess('');
                    }}
                  >
                    OTP
                  </button>
                </div>

              {(loginMethod === 'email' || !isLogin) && (
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required={!isLogin || loginMethod === 'email'}
                    placeholder="your@email.com"
                    data-testid="email-input"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  data-testid="password-input"
                />
              </div>

              {(loginMethod === 'mobile' || !isLogin) && (
                <div className="form-group">
                  <label>Mobile Number / मोबाइल नंबर</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required={!isLogin || loginMethod === 'mobile'}
                    placeholder="+91 XXXXXXXXXX"
                    data-testid="mobile-input"
                  />
                </div>
              )}

              <>

                  <div className="form-group">
                    <label>Preferred Language / पसंदीदा भाषा</label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      data-testid="language-select"
                    >
                      <option value="en">English</option>
                      <option value="hi">हिंदी (Hindi)</option>
                    </select>
                  </div>
                </>

                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Register')}
                </button>
              </form>
            )}

            {isLogin && loginMethod === 'otp' && (
              <div className="auth-form">
                <div className="login-method-toggle">
                  <button
                    type="button"
                    className={loginMethod === 'email' ? 'active' : ''}
                    onClick={() => {
                      setLoginMethod('email');
                      setOtpSent(false);
                      setError('');
                      setSuccess('');
                    }}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    className={loginMethod === 'mobile' ? 'active' : ''}
                    onClick={() => {
                      setLoginMethod('mobile');
                      setOtpSent(false);
                      setError('');
                      setSuccess('');
                    }}
                  >
                    Password
                  </button>
                  <button
                    type="button"
                    className={loginMethod === 'otp' ? 'active' : ''}
                  >
                    OTP
                  </button>
                </div>

                <div className="form-group">
                  <label>Mobile Number / मोबाइल नंबर</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXXXXXXX"
                    disabled={otpSent}
                    data-testid="otp-mobile-input"
                  />
                </div>

                {!otpSent ? (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleRequestOTP}
                    disabled={loading}
                    data-testid="request-otp-btn"
                  >
                    {loading ? 'Sending...' : 'Send OTP / OTP भेजें'}
                  </button>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Enter OTP / OTP दर्ज करें</label>
                      <input
                        type="text"
                        name="otp"
                        value={formData.otp}
                        onChange={handleChange}
                        required
                        placeholder="6-digit OTP"
                        maxLength="6"
                        data-testid="otp-input"
                      />
                    </div>

                    <div className="otp-actions">
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleVerifyOTP}
                        disabled={loading}
                        data-testid="verify-otp-btn"
                      >
                        {loading ? 'Verifying...' : 'Verify OTP / OTP सत्यापित करें'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                          setOtpSent(false);
                          setFormData({ ...formData, otp: '' });
                          setError('');
                          setSuccess('');
                        }}
                        data-testid="resend-otp-btn"
                      >
                        Resend OTP / पुनः भेजें
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {!isLogin && (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    data-testid="email-input"
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                </div>

                <div className="form-group">
                  <label>Mobile Number / मोबाइल नंबर</label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXXXXXXX"
                    data-testid="mobile-input"
                  />
                </div>

                <div className="form-group">
                  <label>Preferred Language / पसंदीदा भाषा</label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    data-testid="language-select"
                  >
                    <option value="en">English</option>
                    <option value="hi">हिंदी (Hindi)</option>
                  </select>
                </div>

                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Please wait...' : 'Register'}
                </button>
              </form>
            )}

            <div className="auth-switch">
              <p>
                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                {' '}
                <button 
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  className="link-button"
                  data-testid="toggle-auth-btn"
                >
                  {isLogin ? 'Register' : 'Login'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
